import { TipoDesconto } from "pages/registra-venda/registra-venda.model";
import { roundHalfToEven, roundToTwoDecimalPlaces } from "./util";

/**
 * Estrutura de retorno para funções de cálculo de desconto.
 */
export type RetornoDesconto = {
  valorDesconto: number;
  percentualDesconto: number;
  valorTotalLiquido: number;
  valorTotalBruto: number;
};

/**
 * Calcula o percentual de desconto com base no valor total bruto.
 *
 * @param valorTotalBruto - Valor total sem desconto.
 * @param valor - Valor do desconto aplicado.
 * @returns Percentual correspondente ao valor do desconto.
 */
export const calcularPorcentagemDesconto = (
  valorTotalBruto: number,
  valor: number
): number => {
  let toReturn = 0;
  if (valorTotalBruto > 0) {
    const valorCalculado = (valor * 100) / valorTotalBruto;
    toReturn = roundHalfToEven(valorCalculado, 2);
  }
  return toReturn;
};

/**
 * Calcula o valor de desconto com base em um percentual.
 *
 * @param valorTotalBruto - Valor total sem desconto.
 * @param porcentagem - Percentual de desconto a ser aplicado.
 * @returns Valor do desconto em reais.
 */
export const calcularValorDesconto = (
  valorTotalBruto: number,
  porcentagem: number
): number => {
  const toReturn = (valorTotalBruto * porcentagem) / 100;
  return roundHalfToEven(toReturn, 2);
};

/**
 * Calcula o valor total bruto de um item (sem desconto).
 *
 * @param quantidade - Quantidade de itens.
 * @param preco - Preço unitário do item.
 * @returns Valor total bruto.
 */
export const getValorTotalBruto = (
  quantidade: number,
  preco: number
): number => {
  return roundToTwoDecimalPlaces(quantidade * preco);
};

/**
 * Calcula o valor total líquido de um item (com desconto).
 *
 * @param quantidade - Quantidade de itens.
 * @param preco - Preço unitário do item.
 * @param percentualDesconto - Percentual de desconto aplicado.
 * @returns Valor total com desconto aplicado.
 */
export const getValorTotalLiquido = (
  quantidade: number,
  preco: number,
  valorDesconto: number
): number => {
  return roundHalfToEven(getValorTotalBruto(quantidade, preco) - valorDesconto, 2)
};

/**
 * Retorna os valores de desconto, total bruto e líquido com base em um percentual de desconto.
 *
 * @param quantidade - Quantidade de itens.
 * @param preco - Preço unitário do item.
 * @param percentualDesconto - Percentual de desconto aplicado.
 * @returns Objeto com os valores calculados.
 */
export const getPorcentagemDesconto = (
  quantidade: number,
  preco: number,
  percentualDesconto: number
): RetornoDesconto => {
  const valorTotalBruto = getValorTotalBruto(quantidade, preco);
  const valorDesconto = calcularValorDesconto(valorTotalBruto, percentualDesconto);
  return {
    percentualDesconto: percentualDesconto,
    valorDesconto,
    valorTotalLiquido: getValorTotalLiquido(
      quantidade,
      preco,
      valorDesconto
    ),
    valorTotalBruto,
  };
};

/**
 * Retorna os valores de desconto, total bruto e líquido com base em um valor absoluto de desconto.
 *
 * @param quantidade - Quantidade de itens.
 * @param preco - Preço unitário do item.
 * @param valorDesconto - Valor absoluto de desconto.
 * @returns Objeto com os valores calculados.
 */
export const getValorDescontoRow = (
  quantidade: number,
  preco: number,
  valorDesconto: number
): RetornoDesconto => {
  const valorTotalBruto = getValorTotalBruto(quantidade, preco);
  const percentualDesconto = calcularPorcentagemDesconto(
    valorTotalBruto,
    valorDesconto
  );

  return {
    valorDesconto,
    percentualDesconto,
    valorTotalLiquido: getValorTotalLiquido(
      quantidade,
      preco,
      valorDesconto,
    ),
    valorTotalBruto,
  };
};

/**
 * Calcula o desconto aplicado (valor e percentual), total bruto e líquido com base no tipo de desconto.
 *
 * @param quantidade - Quantidade de itens.
 * @param preco - Preço unitário do item.
 * @param value - Valor a ser usado no cálculo (percentual ou valor absoluto).
 * @param tipoDesconto - Tipo de desconto (PERCENTUAL ou VALOR).
 * @returns Objeto com os valores de desconto, total bruto e total líquido.
 */
export const getDescItem = (
  quantidade: number,
  preco: number,
  value: number,
  tipoDesconto: TipoDesconto
) => {
  if (tipoDesconto === TipoDesconto.PERCENTUAL) {
    return getPorcentagemDesconto(quantidade, preco, value);
  }
  return getValorDescontoRow(quantidade, preco, value);
};
